import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'

import { AsyncStateN } from '@blade/std-lib'
import { trackingWalletsByAddressAtom } from '@root/features/wallet-tracker/atoms/reatom-tracking-wallets'

import { holderTone, makerRows } from '../../model/maker-rows'
import {
  count,
  percent,
  quantity,
  shortAddress,
  signedUsd,
  usd,
} from '../portfolio-screen/money'

import { reatomPairTopTraders } from './pair-makers'

import type { HoldersResource } from './pair-makers'
import type { RawMaker } from '../../model/maker-rows'
import type { Network } from '@root/entities/network'
import type { PairAddress, PairModel } from '@root/entities/pair'

const toneVar = (tone: 'quiet' | 'watch' | 'bad') =>
  ({ quiet: 'var(--t2)', watch: 'var(--warn)', bad: 'var(--down)' })[tone]

/**
 * Who holds this token, ranked.
 *
 * The same ladder the risk panel colours by, so a wallet reading twelve per
 * cent here is the same red it is there. What the competition cannot print is
 * the two marks on the left: the wallet that launched this, and the wallets
 * you chose to follow.
 */
export const HoldersTable: React.FC<{
  pair: PairModel
  holders: HoldersResource
}> = memo(({ pair, holders }) => {
  const [state] = useAtom((ctx) => ctx.spy(holders), [holders])
  const rows = useMarkedRows(pair, state.value)

  return (
    <MakerTable
      rows={rows}
      loading={AsyncStateN.isLoading(state)}
      empty="Nobody is holding this yet."
      columns={['#', 'HOLDER', 'HELD', 'SHARE', 'VALUE', 'P&L']}
      cell={(row) => [
        <span className="mono rk" key="r">
          {row.rank}
        </span>,
        <Who key="w" row={row} />,
        <span className="mono" key="h">
          {quantity(row.heldTokens)}
        </span>,
        <span
          className="mono"
          key="s"
          style={{ color: toneVar(holderTone(row.sharePercent)) }}
        >
          {percent(row.sharePercent)}
        </span>,
        <span className="mono" key="v">
          {usd(row.heldUsd)}
        </span>,
        <Pnl key="p" value={row.totalPnlUsd} />,
      ]}
    />
  )
})

/**
 * Who has actually made money here.
 *
 * Realised and unrealised are shown apart as well as together, because a
 * wallet that has taken its money out and one that is still holding the same
 * gain are not the same wallet to copy.
 */
export const TopTradersTable: React.FC<{
  pair: PairModel
  pairAddress: PairAddress
  network: Network.Network
}> = memo(({ pair, pairAddress, network }) => {
  const resource = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomPairTopTraders({ pairAddress, network }),
    [pairAddress, network],
  )
  const [state] = useAtom((ctx) => ctx.spy(resource), [resource])
  const rows = useMarkedRows(pair, state.value)

  return (
    <MakerTable
      rows={rows}
      loading={AsyncStateN.isLoading(state)}
      empty="Nobody has closed a trade on this yet."
      columns={[
        '#',
        'TRADER',
        'BOUGHT/SOLD',
        'VOLUME',
        'REALISED',
        'TOTAL P&L',
      ]}
      cell={(row) => [
        <span className="mono rk" key="r">
          {row.rank}
        </span>,
        <Who key="w" row={row} />,
        <span className="mono bs" key="b">
          <em className="good">{row.buys}</em>
          <i>/</i>
          <em className="bad">{row.sells}</em>
        </span>,
        <span className="mono" key="v">
          {usd(row.volumeUsd)}
        </span>,
        <Pnl key="rl" value={row.realisedUsd} />,
        <Pnl key="p" value={row.totalPnlUsd} />,
      ]}
    />
  )
})

/**
 * What else this wallet has put on the market.
 *
 * No request: the creator's tokens travel with the pair. The column that
 * matters is the last one — what each was worth at its best against what it is
 * worth now, which is this dev's record stated without adjectives.
 */
export const DevTokensTable: React.FC<{ pair: PairModel }> = memo(
  ({ pair }) => {
    const [creatorStats] = useAtom(pair.creatorStats)

    const others = (creatorStats?.topTokens ?? []).filter(
      (token) => String(token.address) !== String(pair.quote.address),
    )

    if (others.length === 0) {
      return (
        <p className="soon">
          This is the only token we have seen from this wallet.
        </p>
      )
    }

    return (
      <div className="mkscroll">
        <table className="tb2">
          <thead>
            <tr>
              <th>TOKEN</th>
              <th>MARKET CAP</th>
              <th>ATH</th>
              <th>TRADERS 24H</th>
            </tr>
          </thead>
          <tbody>
            {others.map((token) => (
              <tr key={String(token.address)}>
                <td>{token.currencyModel.ticker}</td>
                <td>{usd(Number(token.currentMarketCap))}</td>
                <td>{usd(Number(token.athMarketCap))}</td>
                <td>{count(Number(token.traders24h))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  },
)

/** The dev and the followed wallets, marked once and used by both tables. */
const useMarkedRows = (
  pair: PairModel,
  makers: ReadonlyArray<RawMaker> | undefined,
) => {
  const [tracking] = useAtom(trackingWalletsByAddressAtom)
  const [audit] = useAtom(pair.audit)
  const deployer = audit.meta.deployerAddress

  return useMemo(
    () =>
      makerRows(makers ?? [], {
        dev: [deployer ? String(deployer) : undefined],
        tracked: Object.keys(tracking),
      }),
    [makers, deployer, tracking],
  )
}

const Who: React.FC<{
  row: { address: string; isDev: boolean; isTracked: boolean }
}> = memo(({ row }) => (
  <span className="who">
    {row.isTracked ? (
      <svg
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="#93a4ff"
        aria-label="A wallet you follow"
      >
        <path d="M12 3 L14.6 8.7 L21 9.5 L16.4 13.8 L17.6 20 L12 17 L6.4 20 L7.6 13.8 L3 9.5 L9.4 8.7 Z" />
      </svg>
    ) : undefined}
    {row.isDev ? <span className="devmark">DEV</span> : undefined}
    <a className="mono maker" href={`/portfolio-v2?wallet=${row.address}`}>
      {shortAddress(row.address)}
    </a>
  </span>
))

/** Grey for nothing and for nothing known; a sign only where there is one. */
const pnlColour = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) {
    return 'var(--t3)'
  }
  return value > 0 ? 'var(--up)' : 'var(--down)'
}

const Pnl: React.FC<{ value: number }> = memo(({ value }) => (
  <span className="mono" style={{ color: pnlColour(value) }}>
    {signedUsd(value)}
  </span>
))

function MakerTable<T extends { key: string; isTracked: boolean }>({
  rows,
  loading,
  empty,
  columns,
  cell,
}: {
  rows: ReadonlyArray<T>
  loading: boolean
  empty: string
  columns: ReadonlyArray<string>
  cell: (row: T) => ReadonlyArray<React.ReactNode>
}) {
  if (rows.length === 0) {
    return <p className="soon">{loading ? 'Reading the wallets…' : empty}</p>
  }

  return (
    <div className="mkscroll">
      <table className="tb2 mk">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.isTracked ? 'lit' : undefined}>
              {cell(row).map((node, index) => (
                <td key={columns[index] ?? String(index)}>{node}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
